import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CopyIcon from '@atlaskit/icon/core/copy';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { Link } from 'react-resource-router';

import {
	ROVO_DEV_CLI_INTRO_URL,
	ROVO_DEV_CLI_LINUX_INSTRUCTIONS_URL,
	ROVO_DEV_CLI_MAC_INSTRUCTIONS_URL,
	ROVO_DEV_CLI_READ_MORE_URL,
	ROVO_DEV_CLI_TOKENS_URL,
	ROVO_DEV_CLI_WINDOWS_INSTRUCTIONS_URL,
} from '../../../../utils/constants';

import messages from './messages';
import { RovoDevCLIInstructionsImage } from './rovo-dev-cli-instructions-image';

export const RovoDevCLIInstructions = () => {
	const [showTooltip, setShowTooltip] = useState<boolean>(false);
	const { formatMessage } = useIntl();

	const showCopiedStatus = () => {
		setShowTooltip(true);
		setTimeout(() => {
			setShowTooltip(false);
		}, 500);
	};

	return (
		<Box xcss={instructionsWrapperStyles}>
			<Stack xcss={textWrapperStyles}>
				<Heading size="xsmall">{formatMessage(messages.heading)}</Heading>
				<Text as="p">
					<FormattedMessage
						id={messages.description.id}
						defaultMessage={messages.description.defaultMessage}
						values={{
							link: (chunks) => (
								<Link target="_blank" href={ROVO_DEV_CLI_INTRO_URL}>
									{chunks}
								</Link>
							),
						}}
					/>{' '}
					{formatMessage(messages.stepOne)}:
				</Text>
				<Box as="ol" xcss={listStyles}>
					<Box as="li">
						{formatMessage(messages.stepTwo)}:
						<Box as="ul" xcss={nestedListStyles}>
							<Box as="li">
								<Link target="_blank" href={ROVO_DEV_CLI_MAC_INSTRUCTIONS_URL}>
									macOS
								</Link>
							</Box>
							<Box as="li">
								<Link target="_blank" href={ROVO_DEV_CLI_LINUX_INSTRUCTIONS_URL}>
									Linux
								</Link>
							</Box>
							<Box as="li">
								<Link target="_blank" href={ROVO_DEV_CLI_WINDOWS_INSTRUCTIONS_URL}>
									Windows
								</Link>
							</Box>
						</Box>
					</Box>
					<Box as="li">
						<FormattedMessage
							id={messages.stepThree.id}
							defaultMessage={messages.stepThree.defaultMessage}
							values={{
								link: (chunks) => (
									<Link target="_blank" href={ROVO_DEV_CLI_TOKENS_URL}>
										{chunks}
									</Link>
								),
							}}
						/>
					</Box>
					<Box as="li">
						{formatMessage(messages.stepFour)}:
						<Box xcss={codeExampleStyles}>
							<Box>acli rovodev auth login</Box>
							<CopyToClipboard text="acli rovodev auth login">
								<Tooltip
									content={showTooltip ? formatMessage(messages.copiedTooltip) : undefined}
									position="right"
								>
									<IconButton
										icon={CopyIcon}
										label=""
										appearance="subtle"
										onClick={showCopiedStatus}
									/>
								</Tooltip>
							</CopyToClipboard>
						</Box>
					</Box>
					<Box as="li">
						{formatMessage(messages.stepFive)}:
						<Box xcss={codeExampleStyles}>
							<Box>acli rovodev run</Box>
							<CopyToClipboard text="acli rovodev run">
								<Tooltip
									content={showTooltip ? formatMessage(messages.copiedTooltip) : undefined}
									position="right"
								>
									<IconButton
										icon={CopyIcon}
										label=""
										appearance="subtle"
										onClick={showCopiedStatus}
									/>
								</Tooltip>
							</CopyToClipboard>
						</Box>
					</Box>
				</Box>
				<Link target="_blank" href={ROVO_DEV_CLI_READ_MORE_URL}>
					{formatMessage(messages.learnMoreToUseLinkTitle)}
				</Link>
			</Stack>
			<Box xcss={imageWrapperStyles}>
				<RovoDevCLIInstructionsImage />
			</Box>
		</Box>
	);
};

const instructionsWrapperStyles = xcss({
	display: 'flex',
	backgroundColor: 'color.background.neutral',
	flexDirection: 'row',
	borderRadius: '3px',
});

const codeExampleStyles = xcss({
	backgroundColor: 'elevation.surface.raised',
	fontFamily: 'font.family.code',
	whiteSpace: 'pre',
	borderRadius: '6px',
	borderWidth: '1px',
	borderColor: 'color.border',
	gap: 'space.100',
	alignItems: 'center',
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
	paddingRight: 'space.100',
	paddingLeft: 'space.100',
	display: 'flex',
	marginTop: 'space.150',
	width: 'fit-content',
});

const textWrapperStyles = xcss({
	gap: 'space.150',
	padding: 'space.250',
	width: 'fit-content',
});

const imageWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'flex-start',
	flexDirection: 'column',
});

const nestedListStyles = xcss({
	listStyleType: 'disc',
	paddingLeft: '0',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.150',
	paddingTop: 'space.150',
});

const listStyles = xcss({
	gap: 'space.150',
	display: 'flex',
	flexDirection: 'column',
});
